import React from 'react'
import _, { trim } from 'lodash'
import * as chrono from 'chrono-node'
import moment from 'moment'
import { Icon, Spinner, Tooltip } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'

import { CardHeaderItem } from 'browser/components/atomic-elements/atoms/card/card-header-item'
import { Entity } from 'shared-libs/models/entity'
import { AbstractCollection } from 'shared-libs/models/abstract-collection'
import apis from 'browser/app/models/apis'
import { createEntityTableColumns, filterTableColumns, getSearchablePathsForColumn } from 'shared-libs/components/table/utils'

import 'browser/components/atomic-elements/organisms/view-header/_view-search.scss'

export enum ViewSearchMode {
  AllIndexed = 'allIndexed',
  VisibleColumns = 'visibleColumns',
}

export function isViewSearchFilter(f: any): boolean {
  return f._isViewSearchFilter === true
}

const CHRONO_UNIT_MAP: Partial<Record<chrono.Component, moment.unitOfTime.StartOf>> = {
  hour: 'hour',
  day: 'day',
  weekday: 'day',
  month: 'month',
  year: 'year',
}

interface IViewSearchProps {
  filters: any[]
  onFiltersChange: (filters: any[]) => void
  dataSet: any
  view: Entity
}

interface IViewSearchState {
  searchText: string
  isSearchLoading: boolean
  hasSearched: boolean
  searchableColumnPaths: any[]
  datetimeColumnPaths: string[]
  nonSearchableColumns: any[]
}

export class ViewSearch extends React.Component<IViewSearchProps, IViewSearchState> {
  private collectionSub: any
  private filtersWithoutSearch: any[]
  constructor(props: IViewSearchProps) {
    super(props)
    this.filtersWithoutSearch = (props.filters || []).filter(f => !isViewSearchFilter(f))
    this.state = {
      searchText: '',
      isSearchLoading: false,
      hasSearched: false,
      searchableColumnPaths: [],
      datetimeColumnPaths: [],
      nonSearchableColumns: [],
    }
  }

  public componentDidMount() {
    this.subscribeToCollection(this.props.dataSet)
    this.resolveSearchableColumns(this.props)
  }

  public componentDidUpdate(prevProps: IViewSearchProps, prevState: IViewSearchState) {
    if (prevProps.dataSet !== this.props.dataSet) {
      this.unsubscribeFromCollection()
      this.subscribeToCollection(this.props.dataSet)
      this.resolveSearchableColumns(this.props)
    }
    if (prevProps.filters !== this.props.filters) {
      this.filtersWithoutSearch = (this.props.filters || []).filter(f => !isViewSearchFilter(f))
    }
    const textChanged = prevState.searchText !== this.state.searchText
    const columnsChanged = prevState.searchableColumnPaths !== this.state.searchableColumnPaths
    if (textChanged || columnsChanged) {
      this.debouncedFilterUpdate()
    }
  }

  public componentWillUnmount() {
    this.debouncedFilterUpdate.cancel()
    this.unsubscribeFromCollection()
  }

  public render() {
    const { searchText, isSearchLoading } = this.state
    return (
      <CardHeaderItem className="c-cardHeader-item--smallMargin c-viewSearch-item">
        <div className="c-viewSearch">
          <Icon className="c-viewSearch-icon" icon={IconNames.SEARCH} />
          <input
            className="c-viewSearch-input"
            placeholder="Search"
            value={searchText}
            onChange={this.handleSearchChange}
          />
          <div className="c-viewSearch-controls">
            {searchText && (
              isSearchLoading
                ? <span><Spinner size={14} /></span>
                : <span className="c-viewSearch-clear" onClick={() => this.setState({ searchText: '' })}><Icon icon={IconNames.CROSS} /></span>
            )}
            {this.renderSearchInfoTooltip()}
          </div>
        </div>
      </CardHeaderItem>
    )
  }

  /**
   * Shows an info icon after the first search that, when hovered, explains date query syntax
   * and lists any columns excluded from the search filter.
   */
  private renderSearchInfoTooltip() {
    const { hasSearched, datetimeColumnPaths, nonSearchableColumns } = this.state

    const hasDates = datetimeColumnPaths.length > 0
    const hasNonSearchable = nonSearchableColumns.length > 0

    if (
      this.getEffectiveSearchMode() !== ViewSearchMode.VisibleColumns ||
      !hasSearched ||
      (!hasDates && !hasNonSearchable)
    ) {
      return null
    }

    const content = (
      <div className="c-viewSearch-tooltip">
        {hasDates && (
          <div className="c-viewSearch-tooltip-section">
            <em>Date fields can be searched using queries like:</em>
            <code>4/5/26</code>
            <code>January 16</code>
            <code>January 2026</code>
            <code>3pm</code>
            <code>10am to 2pm</code>
            <code>01/02/26 to 04/05/26</code>
            <code>yesterday</code>
            <code>today</code>
            <code>today at 6am</code>
          </div>
        )}
        {hasNonSearchable && (
          <div className="c-viewSearch-tooltip-section">
            <em>The following fields are not searchable: </em>
            {nonSearchableColumns.map((col) => col.label || col.path).join(', ')}
          </div>
        )}
      </div>
    )

    return (
      <span className="c-viewSearch-info-icon">
        <Tooltip content={content}>
          <Icon icon={IconNames.INFO_SIGN} />
        </Tooltip>
      </span>
    )
  }


  /**
   * Attempts to parse text as a date/time using chrono-node. Returns a [start, end] range 
   * granularity matches what the user actually typed — hour window for "10am", full day for "Jan 15", 
   * full month for "January", full year for "2024". Explicit ranges like "10am to 2pm" use the parsed 
   * start and end directly. Returns null if no date-like text is found.
   * Important quirk with chrono: it doesnt support parsing open ended ranges like "after Jan 15", 
   * the "after" would be ignored and chrono would return the parse result for "Jan 15", 
   * and we would treat that as a single day range.
   */ 
  private parseDateRange(text: string): { start: Date; end: Date } | null {
    const results = chrono.parse(text)
    if (!results.length) { return null }
    const result = results[0]

    if (result.end) {
      return { start: result.start.date(), end: result.end.date() }
    }

    const start = result.start
    const m = moment(start.date())

    // This logic builds our filter windows based on what chrono was able to parse.
    // It will try to use the smallest granularity that chrono was able to identify.
    // For example, if the text was "Jan 15", chrono can identify the day, but not the time, 
    // so our filter would be the entire day of Jan 15.
    for (const [key, unit] of Object.entries(CHRONO_UNIT_MAP)) {
      if (start.isCertain(key as chrono.Component)) {
        return {
          start: m.clone().startOf(unit).toDate(),
          end: m.clone().endOf(unit).toDate()
        }
      }
    }
    return null
  }

  /**
   * Resolves the view's column definitions against the entity schema and partitions them into
   * searchable text paths, datetime paths, and non-searchable columns for tooltip display.
   * Re-runs whenever the dataSet changes (e.g. view navigation).
   */
  private async resolveSearchableColumns(props: IViewSearchProps) {
    const { dataSet, view } = props
    if (!dataSet?.entitySchema) { return }
    const rawColumns = view?.get('view.state.columns') ?? []
    const resolved = await createEntityTableColumns(apis, dataSet.entitySchema, rawColumns)
    const columns = filterTableColumns(resolved, apis.getSettings())
    const searchableColumnPaths = []
    const datetimeColumnPaths = []
    const nonSearchableColumns = []
    for (const col of columns) {
      const paths = col.viewSearchPaths ?? getSearchablePathsForColumn(col)
      if (!_.isEmpty(paths) && !col.removeFromViewSearch) {
        if (col.schema?.format == 'date-time') {
          datetimeColumnPaths.push(...paths)
          continue
        }
        searchableColumnPaths.push(...paths)
      } else {
        nonSearchableColumns.push(col)
      }
    }
    this.setState({ searchableColumnPaths, datetimeColumnPaths, nonSearchableColumns })
  }

  /**
   * Subscribes to the COLLECTION_LOADED event from the upstream data source, this is how
   * our component knows the search filters have been applied and loading is complete.
   */
  private subscribeToCollection(dataSet: any) {
    const collection = dataSet?.collection
    if (!collection) { return }
    this.collectionSub = collection.addListener(AbstractCollection.COLLECTION_LOADED, () => {
      this.setState({ isSearchLoading: false })
    })
  }

  private unsubscribeFromCollection() {
    this.collectionSub?.remove()
    this.collectionSub = undefined
  }

  private handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchText = e.target.value
    this.setState(prevState => ({ 
      searchText, 
      hasSearched: prevState.hasSearched || !!searchText 
    }))
  }

  private getEffectiveSearchMode(): ViewSearchMode {
    const { view } = this.props

    const configured = view?.get('view.state.viewSearchMode') ?? ViewSearchMode.VisibleColumns
    if (configured !== ViewSearchMode.VisibleColumns) {
      return configured
    }

    const { searchableColumnPaths, datetimeColumnPaths } = this.state
    if (_.isEmpty(searchableColumnPaths) && _.isEmpty(datetimeColumnPaths)) {
      return ViewSearchMode.AllIndexed
    }

    return ViewSearchMode.VisibleColumns
  }

  /**
   * Constructs the filter object for the current search text. In allIndexed mode this is a single _all query. 
   * In visibleColumns mode it's a phrasePrefix multiMatch filter, or an or-filter that
   * combines text and date range filters if applicable.
   */
  private buildSearchFilter(value: string): any {
    const { searchableColumnPaths, datetimeColumnPaths } = this.state

    if (!value) {
      return null
    }

    if (this.getEffectiveSearchMode() === ViewSearchMode.AllIndexed) {
      return {
        path: '_all',
        type: 'query',
        value,
      }
    }

    const multiMatchFilter = {
      type: 'multipleMatch',
      queryMode: 'phrasePrefix',
      value,
      orPaths: _.map(searchableColumnPaths, (path) => ({path}))
    }

    // Visible Columns Search Mode
    const dateRange = this.parseDateRange(value)

    // Build date filters if applicable
    if (dateRange && !_.isEmpty(datetimeColumnPaths)) { 
      const orFilters: any[][] = []
      if (!_.isEmpty(searchableColumnPaths)) {
        orFilters.push([multiMatchFilter])
      }
      _.forEach(datetimeColumnPaths, (path) => {
        orFilters.push([{ path, type: 'range', gte: dateRange.start, lte: dateRange.end }])
      })
      return {
        type: 'or',
        orFilters
      }
    }
    return multiMatchFilter
  }

  /**
   * Builds and propagates search filter upstream for the collection to apply
   */
  private debouncedFilterUpdate = _.debounce(() => {
    const value = this.state.searchText.trim()
    const searchFilter = this.buildSearchFilter(value)
    const hasExistingSearchFilter = (this.props.filters || []).some(isViewSearchFilter)

    // nothing to add or remove — skip to avoid triggering unnecessary upstream reloads
    if (!searchFilter && !hasExistingSearchFilter) {
      return
    }

    // only track loading state when there's a collection that will signal completion
    if (this.collectionSub) {
      this.setState({ isSearchLoading: true })
    }

    if (!searchFilter) {
      this.props.onFiltersChange(this.filtersWithoutSearch)
      return
    }

    searchFilter._isViewSearchFilter = true // For identification

    this.props.onFiltersChange([...this.filtersWithoutSearch, searchFilter])
  }, 500)
}
